import { useSyncExternalStore } from "react";
import { useAuth } from "../../auth/AuthProvider";
import type { Workspace } from "../../api/types";

/**
 * ACT show-all: a member can turn off the account-type filtering for themselves ("show every
 * feature"), which restores the general (unfiltered) UI. Stored per user in this browser only;
 * the admin may forbid it per account (`workspace.allowShowAll === false`).
 */
const KEY_PREFIX = "wato.showAllFeatures.";
const EVENT = "wato:show-all-changed";

export function readShowAll(userId: string): boolean {
  try {
    return globalThis.localStorage?.getItem(KEY_PREFIX + userId) === "1";
  } catch {
    return false;
  }
}

export function writeShowAll(userId: string, on: boolean): void {
  try {
    if (on) globalThis.localStorage?.setItem(KEY_PREFIX + userId, "1");
    else globalThis.localStorage?.removeItem(KEY_PREFIX + userId);
  } catch {
    // Storage may be disabled; the toggle then lasts only until the page reloads.
  }
  globalThis.dispatchEvent?.(new Event(EVENT));
}

export type ShowAllLock = "allow" | "force_on" | "force_off";

/** ACT-14: the admin lock wins over the member's own choice; unknown/absent = `allow`. */
export function resolveShowAll(
  userChoice: boolean,
  allowed: boolean | undefined,
  lock: ShowAllLock | undefined,
): { on: boolean; locked: boolean; lock: ShowAllLock } {
  if (lock === "force_on") return { on: true, locked: true, lock };
  if (lock === "force_off") return { on: false, locked: true, lock };
  return { on: userChoice && allowed !== false, locked: allowed === false, lock: "allow" };
}

/** The type the UI should filter by: `null` (general) while show-all is on and allowed. */
export function effectiveBusinessType(
  businessType: string | null | undefined,
  showAll: boolean,
  allowed: boolean | undefined,
  lock?: ShowAllLock,
): string | null {
  return resolveShowAll(showAll, allowed, lock).on ? null : (businessType ?? null);
}

function subscribe(cb: () => void): () => void {
  globalThis.addEventListener?.(EVENT, cb);
  globalThis.addEventListener?.("storage", cb);
  return () => {
    globalThis.removeEventListener?.(EVENT, cb);
    globalThis.removeEventListener?.("storage", cb);
  };
}

export function useShowAll(
  workspace: Pick<Workspace, "businessType" | "allowShowAll" | "showAllLock">,
): {
  on: boolean;
  allowed: boolean;
  /** ACT-14: the admin fixed the value; the toggle must be hidden. */
  locked: boolean;
  lock: ShowAllLock;
  set: (next: boolean) => void;
  businessType: string | null;
} {
  const { state } = useAuth();
  const userId = state.status === "ready" ? state.me.user.id : "anon";
  const on = useSyncExternalStore(
    subscribe,
    () => readShowAll(userId),
    () => false,
  );
  const resolved = resolveShowAll(on, workspace.allowShowAll, workspace.showAllLock);
  return {
    on: resolved.on,
    allowed: !resolved.locked,
    locked: resolved.locked,
    lock: resolved.lock,
    set: (next) => writeShowAll(userId, next),
    businessType: effectiveBusinessType(
      workspace.businessType,
      on,
      workspace.allowShowAll,
      workspace.showAllLock,
    ),
  };
}
